import React, {createContext, useCallback, useContext, useMemo} from "react";

import {EventEmitter} from "eventemitter3";

// --- Define the new, indexed event payload ---
export interface SetTabbingPayload {
  playerIndex: number;
  isTabbing: boolean;
}

// --- Define the events the coordinator can emit ---
type UserInputEvents = {
  "reset-simulation": () => void;
  "set-tabbing": (payload: SetTabbingPayload) => void; // Event now has a payload object
};

// --- Define the shape of the context API ---
interface UserInputCoordinatorContextState {
  resetSimulation: () => void;
  /**
   * Sets the "tabbing" state for a specific local player input channel.
   * @param playerIndex - The local player channel (0 for primary, 1 for secondary, etc.).
   * @param isTabbing - True if the action is active.
   */
  setTabbing: (playerIndex: number, isTabbing: boolean) => void;
  emitter: EventEmitter<UserInputEvents>;
}

const UserInputCoordinatorContext = createContext<
  UserInputCoordinatorContextState | undefined
>(undefined);

/**
 * A decoupled event bus for user input. It supports multiple input channels
 * for controlling multiple local players.
 */
export const UserInputCoordinatorProvider: React.FC<{
  children: React.ReactNode;
}> = ({children}) => {
  const emitter = useMemo(() => new EventEmitter<UserInputEvents>(), []);

  const resetSimulation = useCallback(() => {
    emitter.emit("reset-simulation");
  }, [emitter]);

  // The setTabbing method now takes the playerIndex
  const setTabbing = useCallback(
    (playerIndex: number, isTabbing: boolean) => {
      emitter.emit("set-tabbing", {playerIndex, isTabbing});
    },
    [emitter],
  );

  const value = useMemo(
    () => ({
      resetSimulation,
      setTabbing,
      emitter,
    }),
    [resetSimulation, setTabbing, emitter],
  );

  return (
    <UserInputCoordinatorContext.Provider value={value}>
      {children}
    </UserInputCoordinatorContext.Provider>
  );
};

export const useUserInput = (): UserInputCoordinatorContextState => {
  const context = useContext(UserInputCoordinatorContext);
  if (!context) {
    throw new Error(
      "useUserInput must be used within a UserInputCoordinatorProvider",
    );
  }
  return context;
};
